import cn from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { ID, withCodes2 } from 'app/blocks/common/codes';
import { compose } from 'app/blocks/common/utils';
import PromptAction from 'app/blocks/Prompt/_action/prompt_action';
import WarningLabel from 'app/blocks/WarningLabel/WarningLabel';
import { paymentInputMaxLength } from 'app/pages/orders/orders.constants';
import payment from 'app/pages/orders/orders.payment';
import CheckboxButton from 'app/ui/form/CheckboxButton';
import { Input2 } from 'app/ui/form/inputs';
import RadioButton from 'app/ui/form/RadioButton';
import StaticContent from '../../../../blocks/static-content/static-content';

function PaymentPanel({
    className,
    isPaymentMethodEditable,
    isPaymentMethodSupportedByBrowser,
    isPoNumberShouldBeDisabled,
    isUpdateCardDetailsCheckboxShouldBeShown,
    l,
    onPaymentMethodChange,
    onPurchaseNumberChange,
    onUpdateCardDetailsFlagChange,
    paymentMethod,
    paymentMethodsAvailable,
    poNumber,
    updateCardDetailsFlag,
}) {
    return (
        <div className={className}>
            <div className="border_top" />
            <div className="order__top_border p-l_none p-r_none">
                {!isPaymentMethodSupportedByBrowser && (
                    <WarningLabel className="mb-base" seleniumId="browser-does-not-support-credit-card-payment-warning">
                        {l('ORDER_PANELS.PANEL_PAYMENT.CREDIT_CARD_PAYMENT_IS_NOT_SUPPORTED_BY_BROWSER')}
                    </WarningLabel>
                )}

                <div className="bold_text m-btm_mini">{l('ORDER_PANELS.PANEL_PAYMENT.PAY_BY')}</div>
                <div>
                    {paymentMethodsAvailable.map((item, index) => (
                        <div key={item.key} className={cn({ 'mt-one-thirds': index > 0 })}>
                            <RadioButton
                                checked={paymentMethod === item.key || paymentMethodsAvailable.length === 1}
                                data-seleniumid={`order-payment-${item.key}-radio-input`}
                                disabled={!isPaymentMethodEditable(item)}
                                id={`order-payment-${item.key}-radio`}
                                label={item.text}
                                labelStyle={{ flex: 'none' }}
                                logo={item.logo ? 'ENABLE' : 'DISABLE'}
                                onChange={e => onPaymentMethodChange(e.target.value)}
                                style={{ height: '30px' }}
                                value={item.key}
                            />

                            {/* eslint-disable-next-line eqeqeq */}
                            {payment.method.CREDIT_CARD == item && isUpdateCardDetailsCheckboxShouldBeShown() && (
                                <CheckboxButton
                                    checked={updateCardDetailsFlag}
                                    className="m-l_md"
                                    data-seleniumid="order-payment-updateCreditCard-checkbox-input"
                                    disabled={paymentMethod !== payment.method.CREDIT_CARD.key}
                                    id="order-payment-updateCreditCard-checkbox"
                                    label={l('ORDER_PANELS.PANEL_PAYMENT.AGREEMENT_UPDATE_CARD')}
                                    onChange={e => onUpdateCardDetailsFlagChange(e.target.checked)}
                                />
                            )}
                        </div>
                    ))}
                </div>
                {paymentMethod === payment.method.SOCOPAY.key && (
                    <StaticContent
                        className="AliPayInfoText"
                        seleniumid="alipay-hep-text"
                        src="alipay-help-text.html"
                    />
                )}

                {!isPoNumberShouldBeDisabled() && (
                    <div className="mt-base">
                        <div className="m-btm_mini">
                            <span>{l('ORDER_PANELS.PANEL_PAYMENT.PURCHASE_ORDER_NUMBER')}</span>
                            <span>&nbsp;(optional)</span>
                            <PromptAction
                                className="base__i-block"
                                html={l('TOOLTIP.orderNumber')}
                                seleniumid="order-payment-poNumber__prompt-icon"
                            />
                        </div>
                        <Input2
                            className="poNumber-input"
                            data-seleniumid="order-payment-poNumber-input"
                            maxLength={paymentInputMaxLength.PO_NUMBER}
                            onChange={onPurchaseNumberChange}
                            placeholder="Optional"
                            type="text"
                            value={poNumber}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

PaymentPanel.propTypes = {
    className: PropTypes.string,
    isPaymentMethodEditable: PropTypes.func.isRequired,
    isPaymentMethodSupportedByBrowser: PropTypes.bool.isRequired,
    isPoNumberShouldBeDisabled: PropTypes.func.isRequired,
    isUpdateCardDetailsCheckboxShouldBeShown: PropTypes.func.isRequired,
    l: PropTypes.func.isRequired,
    onPaymentMethodChange: PropTypes.func.isRequired,
    onPurchaseNumberChange: PropTypes.func.isRequired,
    onUpdateCardDetailsFlagChange: PropTypes.func.isRequired,
    paymentMethod: PropTypes.string.isRequired,
    paymentMethodsAvailable: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    poNumber: PropTypes.string.isRequired,
    updateCardDetailsFlag: PropTypes.bool.isRequired,
};

PaymentPanel.defaultProps = {
    className: undefined,
};

function withPaymentPanelContainer(Component) {
    return function PaymentContainer({ controller, paymentState, ...props }) {
        return (
            <Component
                {...props}
                controller={controller}
                isPaymentMethodEditable={controller.isPaymentMethodEditable}
                isPaymentMethodSupportedByBrowser={controller.isPaymentMethodSupportedByBrowser()}
                isPoNumberShouldBeDisabled={controller.isPoNumberShouldBeDisabled}
                isUpdateCardDetailsCheckboxShouldBeShown={controller.isUpdateCardDetailsCheckboxShouldBeShown}
                onPaymentMethodChange={controller.onPaymentMethodChange}
                onPurchaseNumberChange={controller.onPurchaseNumberChange}
                onUpdateCardDetailsFlagChange={controller.onUpdateCardDetailsFlagChange}
                paymentMethod={paymentState.paymentDetails.paymentMethod}
                paymentMethodsAvailable={controller.paymentMethodsAvailable}
                poNumber={paymentState.paymentDetails.poNumber}
                updateCardDetailsFlag={paymentState.paymentDetails.updateCardDetailsFlag}
            />
        );
    };
}

export { withPaymentPanelContainer };
export default compose(withPaymentPanelContainer, withCodes2(ID.ORDER_PANELS, ID.TOOLTIP))(PaymentPanel);
